
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, UserRole } from '@/context/AuthContext';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

interface NavItem {
  icon: string;
  label: string;
  path: string;
  roles: UserRole[];
  badge?: number;
}

const navItems: NavItem[] = [
  { icon: 'ri-dashboard-line', label: 'Control Panel', path: '/admin-dashboard', roles: ['admin'] },
  { icon: 'ri-team-line', label: 'User Management', path: '/admin-user-management', roles: ['admin'] },
  { icon: 'ri-settings-3-line', label: 'System Config', path: '/admin-system-config', roles: ['admin'] },
  { icon: 'ri-scales-3-line', label: 'Legal Knowledge', path: '/admin-legal-knowledge', roles: ['admin'] },
  { icon: 'ri-notification-3-line', label: 'Notifications', path: '/admin-notifications', roles: ['admin'] },
  { icon: 'ri-file-list-3-line', label: 'Audit Trail', path: '/admin-audit-trail', roles: ['admin'] },
  { icon: 'ri-tools-line', label: 'Maintenance', path: '/admin-maintenance', roles: ['admin'] },

  { icon: 'ri-dashboard-line', label: 'Dashboard', path: '/supervisor-dashboard', roles: ['supervisor'] },
  { icon: 'ri-file-list-3-line', label: 'Victim Reports', path: '/sup-victim-reports', roles: ['supervisor'] },
  { icon: 'ri-user-shared-line', label: 'Case Assignment', path: '/sup-case-assignment', roles: ['supervisor'] },
  { icon: 'ri-checkbox-circle-line', label: 'Case Approvals', path: '/sup-approvals', roles: ['supervisor'] },
  { icon: 'ri-notification-3-line', label: 'Notifications', path: '/sup-notifications', roles: ['supervisor'] },

  { icon: 'ri-dashboard-line', label: 'My Dashboard', path: '/sw-dashboard', roles: ['social_worker'] },
  { icon: 'ri-folder-user-line', label: 'My Cases', path: '/sw-cases', roles: ['social_worker'] },
  { icon: 'ri-file-add-line', label: 'New Case Intake', path: '/case-intake', roles: ['social_worker'] },
  { icon: 'ri-user-search-line', label: 'Child Monitoring', path: '/sw-child-monitoring', roles: ['social_worker'] },
  { icon: 'ri-user-heart-line', label: 'Child Profiles', path: '/child-profiles', roles: ['social_worker'] },
  { icon: 'ri-map-pin-user-line', label: 'Case Scheduling', path: '/sw-fieldwork', roles: ['social_worker'] },
  { icon: 'ri-message-3-line', label: 'Messages', path: '/sw-messages', roles: ['social_worker'] },
  { icon: 'ri-notification-3-line', label: 'Notifications', path: '/sw-notifications', roles: ['social_worker'] },
  { icon: 'ri-clipboard-line', label: 'Assessment', path: '/sw-assessment', roles: ['social_worker'] },
  { icon: 'ri-settings-3-line', label: 'Settings', path: '/settings', roles: ['social_worker'] },

  { icon: 'ri-dashboard-line', label: 'Dashboard', path: '/v-dashboard', roles: ['victim'] },
  { icon: 'ri-file-chart-line', label: 'Report', path: '/v-report', roles: ['victim'] },
  { icon: 'ri-scales-3-line', label: 'Law', path: '/v-law', roles: ['victim'] },
  { icon: 'ri-book-open-line', label: 'Resources', path: '/v-resources', roles: ['victim'] },
  { icon: 'ri-progress-3-line', label: 'Check Progress', path: '/v-progress', roles: ['victim'] },
  { icon: 'ri-notification-3-line', label: 'Notifications', path: '/v-notifications', roles: ['victim'] },
  { icon: 'ri-message-3-line', label: 'Messages', path: '/v-messages', roles: ['victim'] },
];

const roleLabel: Record<UserRole, string> = {
  social_worker: 'Social Worker',
  supervisor: 'Supervisor',
  admin: 'Administrator',
  victim: 'Victim',
};

const roleBadgeColor: Record<UserRole, string> = {
  social_worker: 'bg-teal-100 text-teal-700',
  supervisor: 'bg-amber-100 text-amber-700',
  admin: 'bg-red-100 text-red-700',
  victim: 'bg-purple-100 text-purple-700',
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [approvalCount, setApprovalCount] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [delayedCount, setDelayedCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [profileImage, setProfileImage] = useState('');

  const getRisk = (c: any) => {
    let score = 75;

    switch (c.incident_type) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;
      case 'Physical Abuse':
        score = 90;
        break;
      case 'Emotional Abuse':
        score = 80;
        break;
      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;
      default:
        score = 70;
        break;
    }

    if (c.prior_incidents === 'yes') score += 5;
    if (c.priority_level === 'urgent') score += 3;
    if (c.priority_level === 'emergency') score += 5;

    score = Math.min(score, 100);

    let level = 'low';

    if (score >= 90) level = 'critical';
    else if (score >= 80) level = 'high';
    else if (score >= 75) level = 'moderate';

    return { score, level };
  };

  /*
   * Notification count:
   * - Supervisor: existing supervisor notification API
   * - Social worker: existing social worker notification API
   * - Victim: existing notification API
   */
  useEffect(() => {
    if (!user?.uid) {
      setNotificationCount(0);
      return;
    }

    if (
      user.role !== 'supervisor' &&
      user.role !== 'victim' &&
      user.role !== 'social_worker'
    ) {
      setNotificationCount(0);
      return;
    }

    let cancelled = false;

    const fetchNotifications = async () => {
      try {
        let endpoint = '';
        let parameter = '';

        if (user.role === 'supervisor') {
          endpoint = `${API_BASE}/get_supervisor_notifications.php`;
          parameter = 'supervisor_uid';
        } else if (user.role === 'social_worker') {
          endpoint = `${API_BASE}/get_social_worker_notifications.php`;
          parameter = 'worker_uid';
        } else {
          endpoint = `${API_BASE}/get_notifications.php`;
          parameter = 'uid';
        }

        const response = await fetch(
          `${endpoint}?${parameter}=${encodeURIComponent(user.uid)}&limit=1`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          }
        );

        if (!response.ok) {
          throw new Error(`Notifications request failed: ${response.status}`);
        }

        const result = await response.json();

        if (!result?.success) {
          throw new Error(
            result?.message || 'Failed to retrieve notifications.'
          );
        }

        const unreadCount =
          result?.data?.unread_count ??
          result?.unread_count ??
          0;

        if (!cancelled) {
          setNotificationCount(
            Math.max(0, Number(unreadCount) || 0)
          );
        }
      } catch (error) {
        console.error('Notifications error:', error);

        // Keep the last successful count when a request fails.
      }
    };

    void fetchNotifications();

    const interval = window.setInterval(() => {
      void fetchNotifications();
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [user?.uid, user?.role]);

  useEffect(() => {
    if (!user?.uid) {
      setAlertCount(0);
      return;
    }

    let cancelled = false;

    const fetchAlerts = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/get_alerts.php?uid=${encodeURIComponent(user.uid)}`,
          { cache: 'no-store' }
        );

        if (!response.ok) {
          throw new Error(`Alerts request failed: ${response.status}`);
        }

        const data = await response.json();

        if (cancelled) return;

        if (Array.isArray(data)) {
          setAlertCount(
            data.filter((a: any) => a.read === false).length
          );
        } else {
          setAlertCount(0);
        }
      } catch (error) {
        console.error('Alerts error:', error);
      }
    };

    void fetchAlerts();

    const interval = window.setInterval(() => {
      void fetchAlerts();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [user?.uid]);

  
  useEffect(() => {
    if (!user?.uid || user.role !== 'supervisor') {
      setApprovalCount(0);
      return;
    }

    let cancelled = false;

    const fetchApprovals = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/get_approvals.php?supervisor_uid=${encodeURIComponent(user.uid)}`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
            },
            cache: 'no-store',
          }
        );

        const data = await response.json();

        if (!response.ok || !data?.success) {
          throw new Error(
            data?.error || `Approvals request failed: ${response.status}`
          );
        }

        const approvals = Array.isArray(data.data) ? data.data : [];

        if (!cancelled) {
          setApprovalCount(
            approvals.filter(
              (item: any) => item.status?.toLowerCase() === 'pending'
            ).length
          );
        }
      } catch (error) {
        console.error('Approvals error:', error);
      }
    };

    void fetchApprovals();

    const interval = window.setInterval(() => {
      void fetchApprovals();
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [user?.uid, user?.role]);


  useEffect(() => {
    let cancelled = false;

    const fetchEscalation = async () => {
      try {
        const casesResponse = await fetch(
          `${API_BASE}/get_escalated_cases.php`,
          { cache: 'no-store' }
        );

        if (!casesResponse.ok) {
          throw new Error(
            `Escalated cases request failed: ${casesResponse.status}`
          );
        }

        const casesData = await casesResponse.json();

        if (cancelled) return;

        if (Array.isArray(casesData)) {
          setCriticalCount(
            casesData.filter(
              (c: any) => getRisk(c).level === 'critical'
            ).length
          );
        } else {
          setCriticalCount(0);
        }

        const delayedResponse = await fetch(
          `${API_BASE}/get_delayed_cases.php`,
          { cache: 'no-store' }
        );

        if (!delayedResponse.ok) {
          throw new Error(
            `Delayed cases request failed: ${delayedResponse.status}`
          );
        }

        const delayedData = await delayedResponse.json();

        if (!cancelled) {
          setDelayedCount(
            Array.isArray(delayedData) ? delayedData.length : 0
          );
        }
      } catch (error) {
        console.error('Escalation error:', error);
      }
    };

    void fetchEscalation();

    const interval = window.setInterval(() => {
      void fetchEscalation();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  if (!user) return null;

  const filtered = navItems.filter((item) =>
    item.roles.includes(user.role)
  );

  const seen = new Set<string>();

  const deduped = filtered.filter((item) => {
    const key = `${item.path}-${item.label}`;

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });

  return (
    <aside
      className={`flex h-screen flex-shrink-0 flex-col bg-[#1e2530] transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-5">
        <img
          src="https://public.readdy.ai/ai/img_res/6c7f5cc2-f4c3-4618-bf64-1a758112751d.png"
          alt="DCPCMS"
          className="h-9 w-9 flex-shrink-0 rounded-lg object-contain"
        />

        {!collapsed && (
          <div className="overflow-hidden">
            <p className="text-sm font-bold leading-tight text-white">
              DCPCMS
            </p>
            <p className="text-xs leading-tight text-white/50">
              Child Protection
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => setCollapsed((previous) => !previous)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="ml-auto cursor-pointer text-white/40 transition-colors hover:text-white"
        >
          <i
            className={`${
              collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'
            } text-lg`}
          />
        </button>
      </div>

      {!collapsed && (
        <div className="border-b border-white/10 px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full border border-white/10">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-teal-500 text-sm font-bold text-white">
                  {user.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>

            <div className="overflow-hidden">
              <p className="truncate text-sm font-semibold text-white">
                {user.name}
              </p>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  roleBadgeColor[user.role]
                }`}
              >
                {roleLabel[user.role]}
              </span>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-4">
        {deduped.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path === '/sup-victim-reports' &&
              location.pathname.startsWith('/sup-victim-reports/'));

          const itemBadge =
            item.path === '/sup-notifications' &&
            user.role === 'supervisor'
              ? notificationCount
              : item.path === '/v-notifications'
              ? notificationCount
              : item.path === '/sw-notifications'
              ? notificationCount
              : item.path === '/sw-alerts'
              ? alertCount
              : item.path === '/sup-approvals'
              ? approvalCount
              : item.path === '/sup-escalation'
              ? criticalCount + delayedCount
              : item.badge ?? 0;

          return (
            <button
              key={`${item.path}-${item.label}`}
              type="button"
              onClick={() => navigate(item.path)}
              title={collapsed ? item.label : undefined}
              className={`flex w-full cursor-pointer items-center gap-3 whitespace-nowrap border-l-4 px-4 py-3 text-sm transition-all duration-150 ${
                isActive
                  ? 'border-teal-400 bg-white/10 text-teal-400'
                  : 'border-transparent text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                <i className={`${item.icon} text-lg`} />
              </div>

              {!collapsed && (
                <span className="min-w-0 flex-1 truncate text-left">
                  {item.label}
                </span>
              )}

              {itemBadge > 0 && (
                <span
                  className="flex h-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white"
                  aria-label={`${itemBadge} notifications`}
                >
                  {itemBadge > 99 ? '99+' : itemBadge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="border-t border-white/10 px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-white/50">
            <span className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-teal-400" />
            <span>Online — Synced</span>
          </div>
        </div>
      )}

      <div className="border-t border-white/10 px-4 py-4">
        <button
          type="button"
          onClick={() => {
            logout();
            navigate('/');
          }}
          className="flex w-full cursor-pointer items-center gap-3 whitespace-nowrap text-sm text-white/50 transition-colors hover:text-red-400"
        >
          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
            <i className="ri-logout-box-line text-lg" />
          </div>

          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
